import type { RewardConfig, RewardXpSourceView } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import { XP_SOURCE_COPY } from '@ui/shared/rewards/xpSourceCopy';

/**
 * `XPSourcesTable` (settings-rewards.md §1.1, §8 component inventory) —
 * every XP source, its trigger, and its exact value; a real table
 * (screen-reader navigable, AC-R8/R7.8's "no hidden math"). Sources not yet
 * active (`RewardXpSourceView.active === false` — check-in/habit/review/
 * weekly-target, all Phase 2b+) render with an explicit "Not yet active —
 * arriving with habits" status instead of being silently omitted
 * (phase-2a-rewards.md RW-6's exact requirement), in the supportive voice
 * (a plain statement of what's next, not an apology).
 */
export function XPSourcesTable({
  sources,
  editing,
  config,
  onChange,
}: {
  readonly sources: readonly RewardXpSourceView[];
  readonly editing: boolean;
  readonly config: RewardConfig;
  readonly onChange: (next: RewardConfig) => void;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        Every XP source, when it pays, and its current value
      </caption>
      <thead>
        <tr>
          <Th align="left">Source</Th>
          <Th align="left">When it pays</Th>
          <Th align="right">XP</Th>
        </tr>
      </thead>
      <tbody>
        {sources.map((row) => {
          const copy = XP_SOURCE_COPY[row.source];
          return (
            <tr key={row.source} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: 'var(--space-3) var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <RewardIcon iconId={copy.iconId} size={16} color="var(--text-secondary)" />
                <span style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{copy.label}</span>
              </td>
              <td style={{ padding: 'var(--space-3) var(--space-2)' }}>
                <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>{copy.whenItPays}</span>
                {!row.active && (
                  <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
                    Not yet active — arriving with habits.
                  </div>
                )}
              </td>
              <td style={{ padding: 'var(--space-3) var(--space-2)', textAlign: 'right' }}>
                {editing ? (
                  <input
                    type="number"
                    className="num"
                    min={0}
                    max={999}
                    value={row.value}
                    onChange={(e) =>
                      onChange({
                        ...config,
                        xp: { ...config.xp, [row.source]: Math.max(0, Math.min(999, Number(e.target.value) || 0)) },
                      })
                    }
                    style={{
                      width: '72px',
                      minHeight: 'var(--touch-target-min)',
                      padding: '0 var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--surface-sunken)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--type-body-size)',
                      fontFamily: 'var(--font-family)',
                      textAlign: 'right',
                    }}
                  />
                ) : (
                  <span className="num" style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
                    {row.value}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Th({ children, align }: { readonly children: string; readonly align: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      style={{
        textAlign: align,
        padding: 'var(--space-2)',
        fontSize: 'var(--type-caption-size)',
        color: 'var(--text-muted)',
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}
